import {
  forwardRef,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type RefObject,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, type LinkProps } from 'react-router';
import { IconAlert, IconCheck, IconClose, IconEye, IconEyeOff, IconInfo } from './icons';

const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(' ');
export { cx };

// ---- Panel: the chamfered, 1px-bordered card every page is built from --------

export function Panel({
  children,
  className,
  inner,
  size = 'md',
  tone = 'panel',
  as: As = 'div',
}: {
  children: ReactNode;
  className?: string;
  inner?: string;
  size?: 'sm' | 'md' | 'lg';
  tone?: 'panel' | 'raised' | 'gold';
  as?: 'div' | 'section' | 'article' | 'aside';
}) {
  const cut = size === 'sm' ? 'cut-sm' : size === 'lg' ? 'cut-lg' : 'cut';
  const line = tone === 'gold' ? 'bg-gold/60' : 'bg-line';
  const fill = tone === 'raised' ? 'bg-panel-2' : 'bg-panel';
  return (
    <As className={cx(cut, line, 'p-px', className)}>
      <div className={cx('cut-inner h-full', fill, inner ?? 'p-5 sm:p-6')}>{children}</div>
    </As>
  );
}

// ---- Buttons ----------------------------------------------------------------------

type Variant = 'gold' | 'ghost' | 'danger' | 'quiet';
type Size = 'sm' | 'md' | 'lg';

const sizes: Record<Size, { h: string; pad: string; text: string }> = {
  sm: { h: 'h-9', pad: 'px-3.5 gap-1.5', text: 'text-[0.72rem]' },
  md: { h: 'h-11', pad: 'px-5 gap-2', text: 'text-[0.8rem]' },
  lg: { h: 'h-13', pad: 'px-7 gap-2.5', text: 'text-[0.9rem]' },
};

const outlined = (v: Variant) => v === 'ghost' || v === 'danger';

function buttonClasses(variant: Variant, size: Size, full?: boolean) {
  const s = sizes[size];
  const base = cx(
    'relative inline-flex items-center justify-center font-semibold uppercase tracking-[0.12em] select-none whitespace-nowrap transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-50',
    s.h,
    s.text,
    // Outlined buttons paint only the 1px line on the outer box; Fill pads.
    outlined(variant) ? 'p-px' : s.pad,
    full && 'w-full',
  );
  switch (variant) {
    case 'gold':
      return cx(base, 'cut-sm bg-gold text-on-gold hover:bg-gold-bright shadow-[0_0_24px_-6px_rgb(201_168_92/0.55)]');
    case 'ghost':
      return cx(base, 'cut-sm bg-line-strong text-ink hover:text-gold-bright');
    case 'danger':
      return cx(base, 'cut-sm bg-danger/60 text-danger hover:text-ink');
    case 'quiet':
      return cx(base, 'text-muted hover:text-ink tracking-[0.08em]');
  }
}

/** Ghost and danger buttons draw their border as an outer box; this is the fill. */
function Fill({ variant, size, children }: { variant: Variant; size: Size; children: ReactNode }) {
  if (!outlined(variant)) return <>{children}</>;
  return (
    <span className={cx('cut-inner flex h-full w-full items-center justify-center bg-panel transition-colors hover:bg-panel-2', sizes[size].pad)}>
      {children}
    </span>
  );
}

export const Button = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: Size; full?: boolean; busy?: boolean }
>(function Button({ variant = 'gold', size = 'md', full, busy, className, children, disabled, type = 'button', ...rest }, ref) {
  return (
    <button ref={ref} type={type} className={cx(buttonClasses(variant, size, full), className)} disabled={disabled || busy} aria-busy={busy || undefined} {...rest}>
      <Fill variant={variant} size={size}>
        {busy ? <Spinner size={14} /> : null}
        {children}
      </Fill>
    </button>
  );
});

export function LinkButton({
  variant = 'gold',
  size = 'md',
  full,
  className,
  children,
  ...rest
}: LinkProps & { variant?: Variant; size?: Size; full?: boolean }) {
  return (
    <Link className={cx(buttonClasses(variant, size, full), className)} {...rest}>
      <Fill variant={variant} size={size}>
        {children}
      </Fill>
    </Link>
  );
}

export function ExternalButton({
  variant = 'gold',
  size = 'md',
  full,
  className,
  children,
  href,
  download,
}: {
  variant?: Variant;
  size?: Size;
  full?: boolean;
  className?: string;
  children: ReactNode;
  href: string;
  download?: boolean;
}) {
  return (
    <a
      href={href}
      className={cx(buttonClasses(variant, size, full), className)}
      target={download ? undefined : '_blank'}
      rel={download ? undefined : 'noopener noreferrer'}
      download={download || undefined}
    >
      <Fill variant={variant} size={size}>
        {children}
      </Fill>
    </a>
  );
}

// ---- Form fields ------------------------------------------------------------------

// Border at 3:1 against the panel (WCAG 1.4.11): at the line token's 1.5:1 the
// fields were hard to find on a phone in daylight.
const inputBase =
  'w-full h-11 bg-panel-3 border border-[rgb(255_255_255/0.38)] px-3.5 text-base text-ink placeholder:text-dim transition-colors focus:border-gold focus:outline-none focus-visible:outline-none disabled:opacity-60';

export function Field({
  label,
  hint,
  error,
  children,
  id,
  aside,
}: {
  label: string;
  hint?: ReactNode;
  error?: string;
  children: (props: { id: string; 'aria-invalid'?: boolean; 'aria-describedby'?: string }) => ReactNode;
  id?: string;
  aside?: ReactNode;
}) {
  const auto = useId();
  const fid = id ?? auto;
  const described = error ? `${fid}-err` : hint ? `${fid}-hint` : undefined;
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={fid} className="text-[0.8125rem] font-semibold uppercase tracking-[0.12em] text-muted">
          {label}
        </label>
        {aside}
      </div>
      {children({ id: fid, 'aria-invalid': error ? true : undefined, 'aria-describedby': described })}
      {error ? (
        <p id={`${fid}-err`} className="text-sm text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={`${fid}-hint`} className="text-sm text-dim">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input({ className, ...rest }, ref) {
  return <input ref={ref} className={cx(inputBase, 'aria-[invalid=true]:border-danger', className)} {...rest} />;
});

/** A password field with a show/hide eye: typing 6-23 characters blind on a phone keyboard is where "wrong password" tickets come from. */
export const PasswordInput = forwardRef<HTMLInputElement, Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>>(function PasswordInput({ className, ...rest }, ref) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input ref={ref} type={show ? 'text' : 'password'} className={cx('pr-12', className)} {...rest} />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        className="absolute right-1 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center text-dim hover:text-ink"
        aria-label={show ? 'Hide password' : 'Show password'}
      >
        {show ? <IconEyeOff size={18} /> : <IconEye size={18} />}
      </button>
    </div>
  );
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Select({ className, children, ...rest }, ref) {
  return (
    <select ref={ref} className={cx(inputBase, 'appearance-none pr-9 bg-[length:12px] bg-[right_0.9rem_center] bg-no-repeat', className)} style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23c9a85c' stroke-width='1.6' fill='none'/%3E%3C/svg%3E\")" }} {...rest}>
      {children}
    </select>
  );
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea({ className, ...rest }, ref) {
  return <textarea ref={ref} className={cx(inputBase, 'h-auto min-h-28 py-2.5 leading-relaxed', className)} {...rest} />;
});

export function Toggle({ checked, onChange, label, description, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; description?: string; disabled?: boolean }) {
  const id = useId();
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <label htmlFor={id} className="font-semibold text-ink">
          {label}
        </label>
        {description ? <p className="mt-0.5 text-sm text-dim">{description}</p> : null}
      </div>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cx('relative mt-0.5 h-8 w-14 shrink-0 rounded-full border transition-colors', checked ? 'border-gold bg-gold/80' : 'border-line-strong bg-ground')}
      >
        <span className={cx('absolute top-[3px] h-6 w-6 rounded-full transition-all', checked ? 'left-[1.6rem] bg-on-gold' : 'left-[3px] bg-muted')} />
      </button>
    </div>
  );
}

// ---- Feedback -----------------------------------------------------------------------

export function Spinner({ size = 18, className }: { size?: number; className?: string }) {
  return (
    <span
      role="status"
      aria-label="Loading"
      className={cx('inline-block animate-spin rounded-full border-2 border-current border-r-transparent', className)}
      style={{ width: size, height: size }}
    />
  );
}

export function Loading({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 py-16 text-dim">
      <Spinner />
      <span>{label}</span>
    </div>
  );
}

export function Alert({ tone = 'info', title, children, className }: { tone?: 'info' | 'warn' | 'danger' | 'ok'; title?: string; children?: ReactNode; className?: string }) {
  const tones = {
    info: 'border-info/40 bg-info/8 text-info',
    warn: 'border-warn/40 bg-warn/8 text-warn',
    danger: 'border-danger/45 bg-danger/8 text-danger',
    ok: 'border-ok/40 bg-ok/8 text-ok',
  } as const;
  const Icon = tone === 'ok' ? IconCheck : tone === 'info' ? IconInfo : IconAlert;
  return (
    <div role={tone === 'danger' ? 'alert' : 'status'} className={cx('flex gap-3 border px-4 py-3', tones[tone], className)}>
      <Icon size={18} className="mt-0.5 shrink-0" />
      <div className="min-w-0 text-sm leading-relaxed">
        {title ? <p className="font-semibold">{title}</p> : null}
        {children ? <div className="text-ink/85">{children}</div> : null}
      </div>
    </div>
  );
}

export function Badge({ children, tone = 'neutral', className }: { children: ReactNode; tone?: 'neutral' | 'gold' | 'ok' | 'danger' | 'info' | 'warn'; className?: string }) {
  const tones = {
    neutral: 'border-line-strong text-muted',
    gold: 'border-gold/60 text-gold-bright',
    ok: 'border-ok/50 text-ok',
    danger: 'border-danger/50 text-danger',
    info: 'border-info/50 text-info',
    warn: 'border-warn/50 text-warn',
  } as const;
  return (
    <span className={cx('inline-flex items-center gap-1 border px-2 py-0.5 text-xs font-semibold uppercase tracking-[0.1em]', tones[tone], className)}>
      {children}
    </span>
  );
}

export function Dot({ on, className }: { on: boolean; className?: string }) {
  return (
    <span
      className={cx('inline-block h-2 w-2 rounded-full', on ? 'bg-ok shadow-[0_0_10px_rgb(95_190_109/0.8)]' : 'bg-danger', className)}
      aria-hidden
    />
  );
}

/**
 * A query that FAILED. Never show an empty state for this: "No resellers yet"
 * when the truth is "could not ask" sends players to the wrong conclusion.
 */
export function LoadError({ error, retry, what = 'this' }: { error?: unknown; retry?: () => void; what?: string }) {
  const detail = error instanceof Error && error.message ? error.message : 'Check your connection and try again.';
  return (
    <div role="alert" className="flex flex-col items-center px-6 py-12 text-center">
      <div className="text-danger">
        <IconAlert size={28} />
      </div>
      <p className="mt-3 font-display text-lg tracking-wide text-ink">Could not load {what}</p>
      <p className="mt-1 max-w-md text-sm text-dim">{detail}</p>
      {retry ? (
        <Button variant="ghost" size="sm" className="mt-4" onClick={retry}>
          Try again
        </Button>
      ) : null}
    </div>
  );
}

export function Empty({ icon, title, children }: { icon?: ReactNode; title: string; children?: ReactNode }) {
  return (
    <div className="flex flex-col items-center px-6 py-14 text-center">
      {icon ? <div className="mb-4 text-gold-dim">{icon}</div> : null}
      <p className="font-display text-lg tracking-wide text-ink">{title}</p>
      {children ? <div className="mt-2 max-w-md text-sm text-dim">{children}</div> : null}
    </div>
  );
}

// ---- Layout pieces ---------------------------------------------------------------------

export function PageHeader({ eyebrow, title, children, actions }: { eyebrow?: string; title: string; children?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="mb-8 flex flex-col gap-4 sm:mb-10 sm:flex-row sm:items-end sm:justify-between">
      <div className="max-w-3xl">
        {eyebrow ? <p className="eyebrow mb-2">&#9670; {eyebrow}</p> : null}
        <h1 className="font-display text-3xl leading-tight tracking-wide text-ink sm:text-4xl">{title}</h1>
        {children ? <div className="mt-3 text-muted leading-relaxed">{children}</div> : null}
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap gap-3">{actions}</div> : null}
    </header>
  );
}

export function SectionTitle({ eyebrow, title, action }: { eyebrow?: string; title: string; action?: ReactNode }) {
  return (
    <div className="mb-5 flex items-end justify-between gap-4">
      <div>
        {eyebrow ? <p className="eyebrow mb-1.5">&#9670; {eyebrow}</p> : null}
        <h2 className="font-display text-2xl tracking-wide text-ink">{title}</h2>
      </div>
      {action}
    </div>
  );
}

export function Container({ children, className, wide }: { children: ReactNode; className?: string; wide?: boolean }) {
  return <div className={cx('mx-auto w-full px-4 sm:px-6', wide ? 'max-w-7xl' : 'max-w-6xl', className)}>{children}</div>;
}

export function Stat({ label, value, sub, tone }: { label: string; value: ReactNode; sub?: ReactNode; tone?: 'gold' | 'ok' | 'info' }) {
  const color = tone === 'gold' ? 'text-gold-bright' : tone === 'ok' ? 'text-ok' : tone === 'info' ? 'text-info' : 'text-ink';
  return (
    <div>
      <p className="text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-dim">{label}</p>
      <p className={cx('mt-1 text-2xl font-semibold', color)}>{value}</p>
      {sub ? <p className="mt-0.5 text-sm text-dim">{sub}</p> : null}
    </div>
  );
}

// ---- Tabs (links, so each tab has its own URL) ------------------------------------------

export function TabLinks({ tabs, className }: { tabs: Array<{ to: string; label: string; end?: boolean }>; className?: string }) {
  return (
    <nav className={cx('-mx-4 mb-6 overflow-x-auto px-4 sm:mx-0 sm:px-0', className)} aria-label="Sections">
      <div className="flex min-w-max gap-1 border-b border-line">
        {tabs.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            end={t.end}
            preventScrollReset
            className={({ isActive }) =>
              cx(
                '-mb-px border-b-2 px-4 py-3 text-[0.78rem] font-semibold uppercase tracking-[0.14em] transition-colors',
                isActive ? 'border-gold text-gold-bright' : 'border-transparent text-dim hover:text-ink',
              )
            }
          >
            {t.label}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

export function Chips<T extends string>({ value, options, onChange, label }: { value: T; options: Array<{ value: T; label: string }>; onChange: (v: T) => void; label: string }) {
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          onClick={() => onChange(o.value)}
          className={cx(
            'min-h-10 border px-3.5 py-2 text-xs font-semibold uppercase tracking-[0.12em] transition-colors',
            value === o.value ? 'border-gold bg-gold/15 text-gold-bright' : 'border-line text-dim hover:border-line-strong hover:text-ink',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ---- Dialog -----------------------------------------------------------------------------

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Modal plumbing shared by Dialog and the phone menu: focus moves in, Tab
 * stays in, Escape closes, focus goes back where it was, and the page behind
 * does not scroll. `onClose` is read through a ref so a parent re-render
 * (every keystroke in a dialog's form) does not re-run this and steal focus.
 */
export function useModal(open: boolean, onClose: () => void, panel: RefObject<HTMLElement | null>, first?: RefObject<HTMLElement | null>) {
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    (first?.current ?? panel.current?.querySelector<HTMLElement>(FOCUSABLE))?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close.current();
        return;
      }
      const box = panel.current;
      if (e.key !== 'Tab' || !box) return;
      const items = [...box.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const firstEl = items[0]!;
      const lastEl = items[items.length - 1]!;
      const inside = box.contains(document.activeElement);
      if (e.shiftKey && (!inside || document.activeElement === firstEl)) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && (!inside || document.activeElement === lastEl)) {
        e.preventDefault();
        firstEl.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      prev?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
}

/**
 * Rendered into <body>, not where it is declared: inside the page it sat under
 * the footer (both are z-10 stacking contexts) and a sticky header's
 * backdrop-filter clipped anything fixed inside it.
 */
export function Dialog({ open, onClose, title, children, wide }: { open: boolean; onClose: () => void; title: string; children: ReactNode; wide?: boolean }) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  useModal(open, onClose, panel, closeRef);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-scrim/80 p-4 backdrop-blur-sm" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={panel} role="dialog" aria-modal="true" aria-label={title} className={cx('w-full animate-rise', wide ? 'max-w-4xl' : 'max-w-lg')}>
        <Panel size="lg" inner="p-0">
          <div className="flex items-center justify-between border-b border-line px-5 py-4">
            <h2 className="font-display text-lg tracking-wide">{title}</h2>
            <button ref={closeRef} type="button" onClick={onClose} className="p-1 text-dim hover:text-ink" aria-label="Close">
              <IconClose />
            </button>
          </div>
          <div className="max-h-[80dvh] overflow-y-auto p-5">{children}</div>
        </Panel>
      </div>
    </div>,
    document.body,
  );
}

export function TextLink({ to, children, className }: { to: string; children: ReactNode; className?: string }) {
  return (
    <Link to={to} className={cx('text-gold-bright underline decoration-gold/40 underline-offset-4 hover:decoration-gold-bright', className)}>
      {children}
    </Link>
  );
}
